import { InputListV2 } from '@/components/form/input-list-v2';
import React, { useMemo } from 'react';
import {
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';
import { Typography } from '../typography';

interface AgeRangeSelectorProps {
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	errors: FieldErrors<FieldValues>;
	defaultMinAge?: string;
	defaultMaxAge?: string;
	disabled?: boolean;
}

export const AgeRangeSelector: React.FC<AgeRangeSelectorProps> = ({
	register,
	watch,
	setValue,
	errors,
	defaultMaxAge,
	defaultMinAge,
	disabled,
}) => {
	const [minAge, maxAge] = watch(['minAge', 'maxAge']); // Watch selected values

	// Generate full age options (18-64 + "65+")
	const fullAgeOptions = useMemo(() => {
		const options = Array.from({ length: 47 }, (_, i) => ({
			label: (18 + i).toString(),
			name: (18 + i).toString(),
		}));
		options.push({ label: '65+', name: '65' });

		return [{ label: 'Select', name: '', placeholder: true }, ...options]; // Add "Select" option first
	}, []);

	// Filtered minAge options (cannot be greater than selected maxAge)
	const minAgeOptions = useMemo(() => {
		return fullAgeOptions.filter((option) => {
			if (!maxAge || maxAge === '65') return true;
			return parseInt(option.name) < parseInt(maxAge);
		});
	}, [maxAge, fullAgeOptions]);

	const maxAgeOptions = useMemo(() => {
		return fullAgeOptions.filter((option) => {
			if (!minAge) return true;
			return parseInt(option.name) > parseInt(minAge);
		});
	}, [minAge, fullAgeOptions]);

	const rules = {
		minAge: {
			required: { value: true, message: 'Required field.' },
		},
		maxAge: {
			required: { value: true, message: 'Required field.' },
		},
	};

	return (
		<div className="w-full">
			<Typography type="regular-16">Age Range</Typography>
			<div className="flex gap-x-[11px] ">
				<InputListV2
					register={register}
					className="w-[145.5px]"
					name="minAge"
					setValueInput={setValue}
					classNameInput="!h-[48px]"
					classNameOptionsContainer="max-h-[145px]"
					options={minAgeOptions}
					rules={rules.minAge}
					error={errors.minAge}
					myDefaultValue={defaultMinAge}
					disabled={disabled}
				/>
				<div className="w-4 h-[1px] bg-black mt-7" />
				<InputListV2
					register={register}
					name="maxAge"
					className="w-[145.5px]"
					setValueInput={setValue}
					classNameInput="!h-[48px]"
					classNameOptionsContainer="max-h-[145px]"
					options={maxAgeOptions}
					rules={rules.maxAge}
					error={errors.maxAge}
					myDefaultValue={defaultMaxAge}
					disabled={disabled}
				/>
			</div>
		</div>
	);
};
